import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    FormControlLabel,
    Alert as MuiAlert,
    Stack,
    Divider,
    Tooltip,
    Fade,
    CircularProgress,
    Snackbar
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    NotificationsActive as ActiveIcon,
    NotificationsOff as InactiveIcon,
    Warning as WarningIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { getAuth } from 'firebase/auth';
import {
    subscribeToAlerts,
    createAlert,
    updateAlert,
    deleteAlert,
    toggleAlertStatus
} from '../services/alertService';
import { DateTime } from 'luxon';

function Alerts() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingAlert, setEditingAlert] = useState(null);
    const [alertToDelete, setAlertToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Form state
    const [formData, setFormData] = useState({
        message: '',
        isActive: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const unsubscribe = subscribeToAlerts((alertsData) => {
            setAlerts(alertsData);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const handleOpenDialog = (alert = null) => {
        if (alert) {
            setEditingAlert(alert);
            setFormData({
                message: alert.message,
                isActive: alert.isActive
            });
        } else {
            setEditingAlert(null);
            setFormData({
                message: '',
                isActive: false
            });
        }
        setFormErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingAlert(null);
        setFormData({ message: '', isActive: false });
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.message.trim()) {
            errors.message = 'Message is required';
        } else if (formData.message.trim().length < 10) {
            errors.message = 'Message must be at least 10 characters';
        } else if (formData.message.trim().length > 500) {
            errors.message = 'Message must be less than 500 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        try {
            const currentUser = getAuth().currentUser;

            if (editingAlert) {
                await updateAlert(editingAlert.firestoreId, {
                    message: formData.message.trim(),
                    isActive: formData.isActive
                });
                setSnackbar({
                    open: true,
                    message: 'Alert updated successfully',
                    severity: 'success'
                });
            } else {
                await createAlert({
                    message: formData.message.trim(),
                    createdBy: currentUser?.uid || 'unknown',
                    isActive: formData.isActive
                });
                setSnackbar({
                    open: true,
                    message: 'Alert created successfully',
                    severity: 'success'
                });
            }

            handleCloseDialog();
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Error: ${error.message}`,
                severity: 'error'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (alert) => {
        try {
            await toggleAlertStatus(alert.firestoreId, alert.isActive);
            setSnackbar({
                open: true,
                message: `Alert ${alert.isActive ? 'deactivated' : 'activated'} successfully`,
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Error toggling alert: ${error.message}`,
                severity: 'error'
            });
        }
    };

    const handleOpenDeleteDialog = (alert) => {
        setAlertToDelete(alert);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setAlertToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!alertToDelete) return;

        try {
            await deleteAlert(alertToDelete.firestoreId);
            setSnackbar({
                open: true,
                message: 'Alert deleted successfully',
                severity: 'success'
            });
            handleCloseDeleteDialog();
        } catch (error) {
            setSnackbar({
                open: true,
                message: `Error deleting alert: ${error.message}`,
                severity: 'error'
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not published';
        return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_MED);
    };

    const activeAlerts = alerts.filter(a => a.isActive);
    const inactiveAlerts = alerts.filter(a => !a.isActive);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress size={60} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                        System Alerts
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage system-wide alerts and notifications for users
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    size="large"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3
                    }}
                >
                    Create Alert
                </Button>
            </Box>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
                <Card sx={{ flex: 1, borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ActiveIcon color="success" />
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                {activeAlerts.length}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Active Alerts
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1, borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InactiveIcon color="action" />
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                {inactiveAlerts.length}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Inactive Alerts
                        </Typography>
                    </CardContent>
                </Card>
                <Card sx={{ flex: 1, borderRadius: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoIcon color="primary" />
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                {alerts.length}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Total Alerts
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Active Alerts Section */}
            {activeAlerts.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ActiveIcon color="success" />
                        Active Alerts
                    </Typography>
                    <Stack spacing={2}>
                        {activeAlerts.map((alert) => (
                            <Fade key={alert.id} in timeout={300}>
                                <Card
                                    sx={{
                                        borderRadius: 2,
                                        borderLeft: 6,
                                        borderLeftColor: 'success.main',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            boxShadow: 4,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Chip
                                                        label="ACTIVE"
                                                        color="success"
                                                        size="small"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Published {formatDate(alert.publishedAt)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                                                    {alert.message}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                    <Divider />
                                    <CardActions sx={{ justifyContent: 'flex-end', px: 2 }}>
                                        <Tooltip title="Deactivate alert">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggleStatus(alert)}
                                                color="warning"
                                            >
                                                <InactiveIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit alert">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(alert)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete alert">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDeleteDialog(alert)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            </Fade>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Inactive Alerts Section */}
            {inactiveAlerts.length > 0 && (
                <Box>
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InactiveIcon color="action" />
                        Inactive Alerts
                    </Typography>
                    <Stack spacing={2}>
                        {inactiveAlerts.map((alert) => (
                            <Fade key={alert.id} in timeout={300}>
                                <Card
                                    sx={{
                                        borderRadius: 2,
                                        borderLeft: 6,
                                        borderLeftColor: 'grey.300',
                                        opacity: 0.85,
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            opacity: 1,
                                            boxShadow: 2,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                    <Chip
                                                        label="INACTIVE"
                                                        color="default"
                                                        size="small"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Created {formatDate(alert.createdAt)}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.6, color: 'text.secondary' }}>
                                                    {alert.message}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                    <Divider />
                                    <CardActions sx={{ justifyContent: 'flex-end', px: 2 }}>
                                        <Tooltip title="Activate alert">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggleStatus(alert)}
                                                color="success"
                                            >
                                                <ActiveIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit alert">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDialog(alert)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete alert">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDeleteDialog(alert)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            </Fade>
                        ))}
                    </Stack>
                </Box>
            )}

            {/* Empty State */}
            {alerts.length === 0 && (
                <Card sx={{ borderRadius: 2, textAlign: 'center', py: 8 }}>
                    <CardContent>
                        <WarningIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                            No Alerts Yet
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Create your first alert to notify users about important updates
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            size="large"
                        >
                            Create Alert
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Create/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: '1.5rem' }}>
                    {editingAlert ? 'Edit Alert' : 'Create New Alert'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Alert Message"
                            multiline
                            rows={4}
                            fullWidth
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            error={!!formErrors.message}
                            helperText={formErrors.message || `${formData.message.length}/500 characters`}
                            placeholder="Enter a clear and concise message for users..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2
                                }
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                    color="success"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        Active Status
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formData.isActive
                                            ? 'Alert will be visible to users immediately'
                                            : 'Alert will be saved as inactive'}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseDialog} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        sx={{ minWidth: 100 }}
                    >
                        {submitting ? <CircularProgress size={24} /> : (editingAlert ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Delete Alert?
                </DialogTitle>
                <DialogContent>
                    <MuiAlert severity="warning" sx={{ mb: 2 }}>
                        This action cannot be undone
                    </MuiAlert>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete this alert? This will permanently remove it from the system.
                    </Typography>
                    {alertToDelete && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                "{alertToDelete.message}"
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseDeleteDialog}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteConfirm}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%', borderRadius: 2 }}
                    elevation={6}
                    variant="filled"
                >
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Container>
    );
}

export default Alerts;
